import React from 'react';
import PropTypes from 'prop-types';
import { LocalizationProvider, MobileDatePicker as MuiDatePicker } from '@material-ui/pickers';
import DayJsAdapter from '@material-ui/pickers/adapter/dayjs';
import ErrorMessage from '../ErrorMessage';
import { useFormContext } from '../Form';
import 'dayjs/locale/nl';
import useRegisterInput from '../Form/useRegisterInput';
import TextField from '../TextField';
import useIntl from '@afosto/utils-react/intl/useIntl';
import translations from './translations';

const ForwardTextField = React.forwardRef((props, ref) => <TextField inputRef={ref} {...props} />);

const DatePicker = ({
  name,
  error,
  errorMessage,
  helperText,
  inline,
  isEditing,
  label,
  value,
  onChange,
  onEdit,
  style,
  variant,
  inputVariant,
  disableFuture,
  disablePast,
  maxDate,
  readOnly,
  register: shouldRegister,
  fullWidth,
  margin,
}) => {
  useRegisterInput(name, shouldRegister);
  const intl = useIntl();

  const formContext = useFormContext();
  const {
    editInline: formInline = false,
    isEditing: formIsEditing = true,
    errors = {},
    setValue,
    watch,
  } = formContext || {};
  const parsedInputProps = {
    inline: inline ?? formInline,
    isEditing: isEditing ?? formIsEditing,
    error: error ?? !!errors[name],
    value: value ?? (watch ? watch(name) : null),
  };
  const isInline = parsedInputProps.inline && !parsedInputProps.isEditing;

  if (isInline || readOnly) {
    parsedInputProps.disabled = true;
    parsedInputProps.InputLabelProps = {
      ...(parsedInputProps.InputLabelProps || {}),
      shrink: true,
    };
  }

  if (typeof parsedInputProps.value === 'string') {
    parsedInputProps.value = new Date(parsedInputProps.value);
  }

  /**
   * Handle the change on the select.
   * @param {string|date} date
   */
  const handleOnChange = date => {
    const newValue = !date || !date.isValid() ? null : new Date(date);

    if (formContext && !onChange) {
      setValue(name, newValue);
      return;
    }

    if (onChange) {
      onChange(newValue);
    }
  };

  /**
   * Return the error message.
   */
  const getErrorMessage = () => {
    if (formContext && !errorMessage) {
      return errors[name] && <ErrorMessage errors={errors} name={name} />;
    }

    return errorMessage;
  };

  return (
    <LocalizationProvider dateAdapter={DayJsAdapter} locale="nl">
      <MuiDatePicker
        renderInput={renderInputProps => (
          <ForwardTextField
            {...renderInputProps}
            variant={inputVariant}
            name={name}
            label={label}
            helperText={parsedInputProps.error ? getErrorMessage() : helperText}
            fullWidth={fullWidth}
            error={parsedInputProps.error}
            InputLabelProps={parsedInputProps.InputLabelProps}
            inline={inline}
            readOnly={readOnly}
            isEditing={isEditing}
            placeholder={isInline ? '' : intl.formatMessage(translations.datePlaceholder)}
            margin={margin}
            onEdit={onEdit}
          />
        )}
        displayStaticWrapperAs={variant}
        autoOk
        disabled={parsedInputProps.disabled}
        inputFormat="DD/MM/YYYY"
        mask="__/__/____"
        maxDate={maxDate}
        disableFuture={disableFuture}
        disablePast={disablePast}
        value={parsedInputProps.value ?? null}
        InputAdornmentProps={{ position: 'start' }}
        onChange={handleOnChange}
        style={style}
        cancelLabel={intl.formatMessage(translations.cancelLabel)}
        clearLabel={intl.formatMessage(translations.clearLabel)}
        okLabel={intl.formatMessage(translations.okLabel)}
        todayLabel={intl.formatMessage(translations.todayLabel)}
        toolbarTitle={intl.formatMessage(translations.toolbarTitle)}
      />
    </LocalizationProvider>
  );
};

DatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  helperText: PropTypes.string,
  inline: PropTypes.bool,
  isEditing: PropTypes.bool,
  onEdit: PropTypes.func,
  label: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  style: PropTypes.object,
  variant: PropTypes.oneOf(['desktop', 'mobile', 'static']),
  disableFuture: PropTypes.bool,
  disablePast: PropTypes.bool,
  readOnly: PropTypes.bool,
  register: PropTypes.bool,
  inputVariant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  fullWidth: PropTypes.bool,
  margin: PropTypes.oneOf(['normal', 'dense', 'none']),
  maxDate: PropTypes.instanceOf(Date),
};

DatePicker.defaultProps = {
  error: undefined,
  errorMessage: undefined,
  helperText: undefined,
  inline: undefined,
  isEditing: undefined,
  onEdit: undefined,
  label: '',
  value: undefined,
  onChange: undefined,
  style: undefined,
  variant: 'static',
  inputVariant: 'outlined',
  disableFuture: true,
  disablePast: false,
  readOnly: false,
  register: true,
  fullWidth: true,
  margin: 'normal',
  maxDate: new Date(2099, 31, 12),
};

export default DatePicker;
